import React, { useEffect, useState } from "react";
import { Card, Input, Button, Row, Col, Form, Checkbox, Tabs } from "antd";
import { updateSettingsFee } from "../../api/SettingApi";
import { useGlobalUI } from "../../context/GlobalUIProvider";

const { TabPane } = Tabs;

export default function FeeConfigurationTabs({ feesData, fetchSettings }) {
  const { message, showLoader, hideLoader } = useGlobalUI();
  const [form] = Form.useForm();

  // separate state to control disabled fields
  const [configState, setConfigState] = useState({});

  useEffect(() => {
    if (feesData && feesData.length > 0) {
      const values = {};
      const state = {};
      feesData.forEach((fee) => {
        values[fee.customerType] = fee;

        // initialize config state
        state[fee.customerType] = {
          flatConfig: fee.flatConfig || false,
          percentageConfig: fee.percentageConfig || false,
          tierConfigEnabled: fee.tierConfigEnabled || false,
        };
      });
      form.setFieldsValue(values);
      setConfigState(state);
    }
  }, [feesData, form]);

  const saveAll = async () => {
    showLoader();
    try {
      const values = await form.validateFields();
      const payload = Object.values(values).map((val, idx) => ({
        ...feesData[idx],
        ...val,
      }));

      await updateSettingsFee(payload);
      fetchSettings();
      message.success("All fees saved successfully");
    } catch (err) {
      message.error("Validation failed");
    } finally {
      hideLoader();
    }
  };

  // exclusive check: update only clicked one
  const handleExclusiveCheck = (customerType, clickedName) => {
    setConfigState((prev) => ({
      ...prev,
      [customerType]: {
        flatConfig: false,
        percentageConfig: false,
        tierConfigEnabled: false,
        [clickedName]: true,
      },
    }));

    form.setFieldsValue({
      [customerType]: {
        ...form.getFieldValue(customerType),
        flatConfig: false,
        percentageConfig: false,
        tierConfigEnabled: false,
        [clickedName]: true,
      },
    });
  };

  const isDisabled = (customerType, parentName) => {
    return !configState[customerType]?.[parentName];
  };

  const customerLabels = {
    1: "Program 1",
    2: "Program 2",
    3: "Program 3",
  };

  return (
    <Form form={form} layout="vertical">
      <Tabs>
        {feesData.map((fee) => (
          <TabPane
            tab={customerLabels[fee.customerType]}
            key={fee.customerType}
          >
            {/* Flat Fee */}
            <Card
              title={
                <Row justify="space-between" align="middle">
                  <Col>Flat Fee Configuration</Col>
                  <Col>
                    <Checkbox
                      checked={configState[fee.customerType]?.flatConfig}
                      onChange={() =>
                        handleExclusiveCheck(fee.customerType, "flatConfig")
                      }
                    />
                  </Col>
                </Row>
              }
              style={{ marginBottom: 24 }}
            >
              <Row gutter={16}>
                <Col span={6}>
                  <Form.Item
                    name={[fee.customerType, "cashInFee"]}
                    label="Cash In Fee"
                    rules={[{ required: true, message: "Enter Cash In Fee" }]}
                  >
                    <Input
                      type="number"
                      min={0}
                      step="0.01"
                      disabled={isDisabled(fee.customerType, "flatConfig")}
                    />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item
                    name={[fee.customerType, "transferFee"]}
                    label="Transfer Fee"
                    rules={[{ required: true, message: "Enter Transfer Fee" }]}
                  >
                    <Input
                      type="number"
                      min={0}
                      step="0.01"
                      disabled={isDisabled(fee.customerType, "flatConfig")}
                    />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item
                    name={[fee.customerType, "purchaseFee"]}
                    label="Purchase Fee"
                    rules={[{ required: true, message: "Enter Purchase Fee" }]}
                  >
                    <Input
                      type="number"
                      min={0}
                      step="0.01"
                      disabled={isDisabled(fee.customerType, "flatConfig")}
                    />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item
                    name={[fee.customerType, "billPaymentFee"]}
                    label="Bill Payment Fee"
                    rules={[
                      { required: true, message: "Enter Bill Payment Fee" },
                    ]}
                  >
                    <Input
                      type="number"
                      min={0}
                      step="0.01"
                      disabled={isDisabled(fee.customerType, "flatConfig")}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Card>

            {/* Percentage Fee */}
            <Card
              title={
                <Row justify="space-between" align="middle">
                  <Col>Percentage Fee Configuration</Col>
                  <Col>
                    <Checkbox
                      checked={configState[fee.customerType]?.percentageConfig}
                      onChange={() =>
                        handleExclusiveCheck(
                          fee.customerType,
                          "percentageConfig"
                        )
                      }
                    />
                  </Col>
                </Row>
              }
              style={{ marginBottom: 24 }}
            >
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item
                    name={[fee.customerType, "percentageStandardRate"]}
                    label="Standard Rate (%)"
                  >
                    <Input
                      type="number"
                      min={0}
                      step="0.01"
                      disabled={isDisabled(
                        fee.customerType,
                        "percentageConfig"
                      )}
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name={[fee.customerType, "percentageMinimumFee"]}
                    label="Minimum Fee"
                  >
                    <Input
                      type="number"
                      min={0}
                      step="0.01"
                      disabled={isDisabled(
                        fee.customerType,
                        "percentageConfig"
                      )}
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name={[fee.customerType, "percentageMaximumFee"]}
                    label="Maximum Fee"
                  >
                    <Input
                      type="number"
                      min={0}
                      step="0.01"
                      disabled={isDisabled(
                        fee.customerType,
                        "percentageConfig"
                      )}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Card>

            {/* Tiered Fee */}
            <Card
              title={
                <Row justify="space-between" align="middle">
                  <Col>Tiered Fee Configuration</Col>
                  <Col>
                    <Checkbox
                      checked={configState[fee.customerType]?.tierConfigEnabled}
                      onChange={() =>
                        handleExclusiveCheck(
                          fee.customerType,
                          "tierConfigEnabled"
                        )
                      }
                    />
                  </Col>
                </Row>
              }
            >
              {/* Tier 1 */}
              <Card
                type="inner"
                title="Tier 1 (Low Value)"
                style={{ marginBottom: 16 }}
              >
                <Row gutter={16}>
                  <Col span={6}>
                    <Form.Item
                      name={[fee.customerType, "tier1MinAmount"]}
                      label="Min Amount"
                    >
                      <Input
                        type="number"
                        min={0}
                        disabled={isDisabled(
                          fee.customerType,
                          "tierConfigEnabled"
                        )}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item
                      name={[fee.customerType, "tier1MaxAmount"]}
                      label="Max Amount"
                    >
                      <Input
                        type="number"
                        min={0}
                        disabled={isDisabled(
                          fee.customerType,
                          "tierConfigEnabled"
                        )}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item
                      name={[fee.customerType, "tier1FlatFee"]}
                      label="Flat Fee"
                    >
                      <Input
                        type="number"
                        min={0}
                        step="0.01"
                        disabled={isDisabled(
                          fee.customerType,
                          "tierConfigEnabled"
                        )}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item
                      name={[fee.customerType, "tier1Percentage"]}
                      label="Percentage (%)"
                    >
                      <Input
                        type="number"
                        min={0}
                        step="0.01"
                        disabled={isDisabled(
                          fee.customerType,
                          "tierConfigEnabled"
                        )}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>

              {/* Tier 2 */}
              <Card
                type="inner"
                title="Tier 2 (Medium Value)"
                style={{ marginBottom: 16 }}
              >
                <Row gutter={16}>
                  <Col span={6}>
                    <Form.Item
                      name={[fee.customerType, "tier2MinAmount"]}
                      label="Min Amount"
                    >
                      <Input
                        type="number"
                        min={0}
                        disabled={isDisabled(
                          fee.customerType,
                          "tierConfigEnabled"
                        )}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item
                      name={[fee.customerType, "tier2MaxAmount"]}
                      label="Max Amount"
                    >
                      <Input
                        type="number"
                        min={0}
                        disabled={isDisabled(
                          fee.customerType,
                          "tierConfigEnabled"
                        )}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item
                      name={[fee.customerType, "tier2FlatFee"]}
                      label="Flat Fee"
                    >
                      <Input
                        type="number"
                        min={0}
                        step="0.01"
                        disabled={isDisabled(
                          fee.customerType,
                          "tierConfigEnabled"
                        )}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item
                      name={[fee.customerType, "tier2Percentage"]}
                      label="Percentage (%)"
                    >
                      <Input
                        type="number"
                        min={0}
                        step="0.01"
                        disabled={isDisabled(
                          fee.customerType,
                          "tierConfigEnabled"
                        )}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>

              {/* Tier 3 */}
              <Card
                type="inner"
                title="Tier 3 (High Value)"
                style={{ marginBottom: 16 }}
              >
                <Row gutter={16}>
                  <Col span={6}>
                    <Form.Item
                      name={[fee.customerType, "tier3MinAmount"]}
                      label="Min Amount"
                    >
                      <Input
                        type="number"
                        min={0}
                        disabled={isDisabled(
                          fee.customerType,
                          "tierConfigEnabled"
                        )}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item
                      name={[fee.customerType, "tier3MaxAmount"]}
                      label="Max Amount"
                    >
                      <Input
                        type="number"
                        min={0}
                        disabled={isDisabled(
                          fee.customerType,
                          "tierConfigEnabled"
                        )}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item
                      name={[fee.customerType, "tier3FlatFee"]}
                      label="Flat Fee"
                    >
                      <Input
                        type="number"
                        min={0}
                        step="0.01"
                        disabled={isDisabled(
                          fee.customerType,
                          "tierConfigEnabled"
                        )}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item
                      name={[fee.customerType, "tier3Percentage"]}
                      label="Percentage (%)"
                    >
                      <Input
                        type="number"
                        min={0}
                        step="0.01"
                        disabled={isDisabled(
                          fee.customerType,
                          "tierConfigEnabled"
                        )}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>
            </Card>
          </TabPane>
        ))}
      </Tabs>

      {/* Global Save Button */}
      <Row justify="end" style={{ marginTop: 24 }}>
        <Button
          type="primary"
          onClick={saveAll}
          style={{ background: "#021B79", color: "white" }}
        >
          Save All Fees
        </Button>
      </Row>
    </Form>
  );
}
